import React, { useState } from 'react';
import '@fortawesome/fontawesome-free/css/all.min.css';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AdList from 'components/AdList/AdList';
import AdFilter from 'components/Filter/AdFilter';
import AdFilterContainer from 'components/Filter/AdFilterContainer';
import FilterDrawer from 'components/Filter/FilterDrawer';
import Button from 'components/Button';
import { useListingSearch } from 'hooks/useListingSearch';
import { useIsMobile } from 'hooks/useMediaQuery';
import type { ListingSortOption } from 'api/utils/listingMapper';
import { SortingOptions } from 'types/sortingOptions';
import { ViewOption } from 'types/viewOptions';
import { favoriteListingService } from 'api/services/FavoriteListingService';
import Navbar from 'components/Header/Navbar';
import Footer from 'components/Footer';

const DEFAULT_PAGE_SIZE = 12;

const Home: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const [viewType, setViewType] = useState<ViewOption>(ViewOption.GRID);
  const isMobile = useIsMobile();
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  const {
    listings,
    totalPages,
    currentPage,
    pageSize,
    loading,
    fetchError,
    topCategories,
    secondaryCategories,
    productTypes,
    constraints,
    constraintsLoading,
    constraintsError,
    conditions,
    cities,
    districts,
    draft,
    applied,
    setDraft,
    setDraftCategory,
    setDraftSpecValue,
    setDraftSpecMultiValue,
    setDraftSpecOperator,
    setDraftPrice,
    applyFilters,
    clearFilters,
    setPage,
    sortOption,
    setSortOption,
    refetch,
  } = useListingSearch(DEFAULT_PAGE_SIZE);

  const handleSortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSortOption(e.target.value as ListingSortOption);
  };

  const handleViewChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setViewType(e.target.value as ViewOption);
  };

  const toggleFavorite = async (ilanId: string | number, e: React.MouseEvent) => {
    e.preventDefault();
    if (!user?.id) return;
    const listingId = String(ilanId);
    const result = await favoriteListingService.toggle({ userId: user.id, listingId });
    if (result.isSuccess) refetch();
  };

  const displayStart = totalPages > 0 ? (currentPage - 1) * pageSize + 1 : 0;
  const displayEnd = displayStart + listings.length - 1;
  const handlePageChange = (pageNumber: number) => {
    setPage(pageNumber);
    window.scrollTo({ top: 400, behavior: 'smooth' });
  };
  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

  return (
        <div className="min-vh-100" style={{ backgroundColor: 'var(--sb-bg-muted)' }}>
            <Navbar />

            {/* Ana İçerik */}
            <main className="container py-4">
                <div className="row">
                    {/* Desktop: sidebar filter */}
                    {!isMobile && (
                        <AdFilterContainer>
                            <AdFilter
                                topCategories={topCategories}
                                secondaryCategories={secondaryCategories}
                                productTypes={productTypes}
                                constraints={constraints}
                                constraintsLoading={constraintsLoading}
                                constraintsError={constraintsError}
                                conditions={conditions}
                                cities={cities}
                                districts={districts}
                                draft={draft}
                                applied={applied}
                                setDraft={setDraft}
                                setDraftCategory={setDraftCategory}
                                setDraftSpecValue={setDraftSpecValue}
                                setDraftSpecMultiValue={setDraftSpecMultiValue}
                                setDraftSpecOperator={setDraftSpecOperator}
                                setDraftPrice={setDraftPrice}
                                onApply={applyFilters}
                                onClearFilters={clearFilters}
                            />
                        </AdFilterContainer>
                    )}

                    <div className="col-12 col-lg-9">
                        {fetchError && (
                            <div className="alert alert-warning mb-3" role="alert">{fetchError}</div>
                        )}
                        {loading ? (
                            <div className="text-center py-5 text-muted">Yükleniyor...</div>
                        ) : (
                            <AdList
                                sortedIlanlar={listings}
                                itemsPerPage={pageSize}
                                currentPage={currentPage}
                                totalPages={totalPages}
                                sortOption={sortOption as SortingOptions}
                                viewType={viewType}
                                handleSortChange={handleSortChange}
                                handleViewChange={handleViewChange}
                                detailLinkBase={isAuthenticated ? '/ilan-detay-login' : '/ilan-detay'}
                                onToggleFavorite={toggleFavorite}
                                displayFavorites={isAuthenticated}
                                headerLeftAddon={isMobile ? (
                                    <Button
                                        type="button"
                                        className="btn btn-outline-primary btn-sm"
                                        onClick={() => setFilterDrawerOpen(true)}
                                    >
                                        <i className="fas fa-filter me-1" />
                                        Filtreler
                                    </Button>
                                ) : undefined}
                            />
                        )}
                    </div>
                </div>
            </main>

            {/* Mobile: filter drawer */}
            {isMobile && (
                <FilterDrawer
                    isOpen={filterDrawerOpen}
                    onClose={() => setFilterDrawerOpen(false)}
                    title="Filtreler"
                >
                    <div className="mb-3">
                        {isAuthenticated && (
                          <Link className="btn btn-primary w-100 mb-2" to="/teknik-destek" onClick={() => setFilterDrawerOpen(false)}>
                            <i className="fas fa-tools me-2" />
                            Teknik Destek
                          </Link>
                        )}
                        <Link className="btn btn-primary w-100 mb-4" to="/gonderiler" onClick={() => setFilterDrawerOpen(false)}>
                            <i className="fas fa-video me-2" />
                            Gönderiler
                        </Link>
                    </div>
                    <AdFilter
                        topCategories={topCategories}
                        secondaryCategories={secondaryCategories}
                        productTypes={productTypes}
                        constraints={constraints}
                        constraintsLoading={constraintsLoading}
                        constraintsError={constraintsError}
                        conditions={conditions}
                        cities={cities}
                        districts={districts}
                        draft={draft}
                        applied={applied}
                        setDraft={setDraft}
                        setDraftCategory={setDraftCategory}
                        setDraftSpecValue={setDraftSpecValue}
                        setDraftSpecMultiValue={setDraftSpecMultiValue}
                        setDraftSpecOperator={setDraftSpecOperator}
                        setDraftPrice={setDraftPrice}
                        onApply={() => {
                            applyFilters();
                            setFilterDrawerOpen(false);
                        }}
                        onClearFilters={clearFilters}
                    />
                </FilterDrawer>
            )}

            {/* Pagination - Moved to bottom of page before footer */}
            {totalPages > 1 && (
                <div className="container mb-5">
                    <div className="pagination-container py-4 border-top border-bottom">
                        <nav aria-label="İlan sayfaları">
                            <ul className="pagination pagination-lg justify-content-center mb-0">
                                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                    <Button 
                                        className="page-link" 
                                        onClick={() => handlePageChange(currentPage - 1)}
                                        disabled={currentPage === 1}
                                    >
                                        <i className="fas fa-chevron-left me-1"></i> Önceki
                                    </Button>
                                </li>
                                
                                {pageNumbers.map(number => (
                                    <li key={number} className={`page-item ${currentPage === number ? 'active' : ''}`}>
                                        <Button 
                                            className="page-link" 
                                            onClick={() => handlePageChange(number)}
                                        >
                                            {number}
                                        </Button>
                                    </li>
                                ))}
                                
                                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                    <Button 
                                        className="page-link" 
                                        onClick={() => handlePageChange(currentPage + 1)}
                                        disabled={currentPage === totalPages}
                                    >
                                        Sonraki <i className="fas fa-chevron-right ms-1"></i>
                                    </Button>
                                </li>
                            </ul>
                        </nav>
                        <div className="text-center text-muted mt-3">
                            <small>
                                {totalPages > 0 && <span>Sayfa <strong>{currentPage}</strong> / <strong>{totalPages}</strong>. </span>}
                                <strong>{displayStart}-{listings.length > 0 ? displayEnd : 0}</strong> arası gösteriliyor
                            </small>
                        </div>
                    </div>
                </div>
            )}

            {/* CSS ekleyelim */}
            <style>
                {`
                .product-card {
                    transition: transform 0.3s ease, box-shadow 0.3s ease;
                }
                
                .product-card:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
                }
                
                .specs-container {
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                }
                
                .spec-item {
                    display: flex;
                    align-items: center;
                    font-size: 0.9rem;
                }
                
                .spec-icon {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 24px;
                    height: 24px;
                    border-radius: 50%;
                    background-color: #f8f9fa;
                }
                
                .favorite-btn {
                    z-index: 10;
                    width: 32px;
                    height: 32px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s;
                }
                
                .favorite-btn:hover {
                    transform: scale(1.1);
                }
                
                .favorite-btn.active .far.fa-heart,
                .favorite-btn.active .fas.fa-heart {
                    color: #dc3545 !important;
                }
                
                /* Pagination Styles */
                .pagination-container {
                    background-color: var(--sb-bg-muted);
                    border-color: var(--sb-border) !important;
                }
                
                .pagination .page-link {
                    color: var(--sb-text);
                    background-color: var(--sb-bg);
                    border-color: var(--sb-border);
                    border-radius: 8px;
                    margin: 0 4px;
                    padding: 12px 20px;
                    transition: all 0.3s ease;
                    font-weight: 600;
                    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
                }
                
                .pagination .page-link:hover {
                    background-color: var(--sb-bg-muted);
                    border-color: var(--sb-border);
                    transform: translateY(-3px);
                    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
                    color: #0d6efd;
                }
                
                .pagination .page-item.active .page-link {
                    background-color: #0d6efd;
                    border-color: #0d6efd;
                    color: white;
                    transform: translateY(-3px);
                    box-shadow: 0 4px 12px rgba(13, 110, 253, 0.3);
                }
                
                .pagination .page-item.disabled .page-link {
                    color: var(--sb-text-muted);
                    pointer-events: none;
                    background-color: var(--sb-bg);
                    border-color: var(--sb-border);
                    opacity: 0.6;
                }
                
                .pagination-lg .page-link {
                    font-size: 1rem;
                }
                
                @media (max-width: 576px) {
                    .pagination .page-link {
                        padding: 8px 12px;
                        font-size: 0.9rem;
                    }
                }

                /* Dropdown Styles */
                .dropdown-menu {
                    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
                    animation: fadeIn 0.3s ease;
                }

                .dropdown-header {
                    font-weight: 600;
                    letter-spacing: 0.5px;
                    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
                }

                .dropdown-item {
                    padding: 0.6rem 1.2rem;
                    transition: all 0.2s;
                }

                .dropdown-item:hover {
                    background-color: var(--sb-dropdown-hover-bg);
                    padding-left: 1.5rem;
                }

                .dropdown-item:active {
                    background-color: #0d6efd;
                    color: white;
                }

                .dropdown-item:active i {
                    color: white !important;
                }

                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(-10px); }
                    to { opacity: 1; transform: translateY(0); }
                }

                /* Navbar Styles */
                .btn-link {
                    text-decoration: none !important;
                    box-shadow: none !important;
                    border: none !important;
                    outline: none !important;
                }

                .btn-link:focus, .btn-link:hover, .btn-link:active {
                    text-decoration: none !important;
                    box-shadow: none !important;
                    border: none !important;
                    outline: none !important;
                }

                .dropdown-toggle::after {
                    vertical-align: middle;
                    margin-left: 0.4em;
                }
                
                /* Notification Styles */
                .notifications-body {
                    scrollbar-width: thin;
                    scrollbar-color: var(--sb-border) var(--sb-bg);
                }
                
                .notifications-body::-webkit-scrollbar {
                    width: 6px;
                }
                
                .notifications-body::-webkit-scrollbar-track {
                    background: var(--sb-bg-muted);
                }
                
                .notifications-body::-webkit-scrollbar-thumb {
                    background-color: var(--sb-border);
                    border-radius: 6px;
                }
                
                .notification-item {
                    transition: all 0.2s ease;
                }
                
                .notification-item:hover {
                    background-color: rgba(13, 110, 253, 0.05);
                }
                
                .notification-icon {
                    transition: transform 0.2s ease;
                }
                
                .notification-item:hover .notification-icon {
                    transform: scale(1.1);
                }
                
                .notifications-footer a {
                    transition: all 0.2s ease;
                }
                
                .notifications-footer a:hover {
                    text-decoration: underline !important;
                }
                
                /* Messages Styles */
                .messages-body {
                    scrollbar-width: thin;
                    scrollbar-color: var(--sb-border) var(--sb-bg);
                }
                
                .messages-body::-webkit-scrollbar {
                    width: 6px;
                }
                
                .messages-body::-webkit-scrollbar-track {
                    background: var(--sb-bg-muted);
                }
                
                .messages-body::-webkit-scrollbar-thumb {
                    background-color: var(--sb-border);
                    border-radius: 6px;
                }
                
                .message-item {
                    transition: all 0.2s ease;
                }
                
                .message-item:hover {
                    background-color: rgba(13, 110, 253, 0.05);
                }
                
                .message-avatar {
                    transition: transform 0.2s ease;
                }
                
                .message-item:hover .message-avatar {
                    transform: scale(1.05);
                }
                
                .messages-footer a {
                    transition: all 0.2s ease;
                }
                
                .messages-footer a:hover {
                    text-decoration: underline !important;
                }

                /* Mesaj ve Bildirim Butonları */
                .nav-link-icon {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    position: relative;
                    border-radius: 50%;
                    transition: all 0.2s ease;
                }

                .nav-link-icon:hover {
                    background-color: rgba(13, 110, 253, 0.1);
                }
                `}
            </style>

            {/* Footer */}
            <Footer />
        </div>
    );
};

export default Home;
